'use client';

import { useEffect, useContext, useState, type FC } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  Flex,
  Loading,
  Heading,
  Separator,
  Text,
} from '@/_components';
import { saveOwnerNotifications } from '@/_api/admin-companies';
import { getCompany } from '@/_api/client-companies';
import type { ClientNotificationsDialogProps } from './ClientNotificationsDialog.types';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import styles from './ClientNotificationsDialog.module.css';
import { User } from '@/_types/models/user';

export const ClientNotificationsDialog: FC<ClientNotificationsDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isLoadingCompany, setLoadingCompany] = useState<boolean>(false);
  const [ownersWithNotifications, setOwnersWithNotifications] = useState<
    Array<any>
  >([]);

  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { selectedCompany } = useContext(CompanyContext);

  useEffect(() => {
    if (isOpen && selectedCompany && ownersWithNotifications.length === 0) {
      const findAssignment = (owner: any) => {
        return owner.assignments.find(
          (assignment: any) => assignment.company_id === selectedCompany.id,
        );
      };

      const fetchCompany = async () => {
        setLoadingCompany(true);
        const { owners } = await getCompany({
          companyId: selectedCompany.id,
          token,
        });

        const mapped: Array<User> =
          owners?.map?.((owner: User) => {
            return {
              ...owner,
              receive_questionnaire:
                findAssignment(owner).notification_type_ids.includes(4),
              sign_questionnaire_signature:
                findAssignment(owner).notification_type_ids.includes(5),
            };
          }) || [];

        setOwnersWithNotifications(mapped);
        setLoadingCompany(false);
      };

      fetchCompany();
    }
  }, [isOpen, ownersWithNotifications.length, selectedCompany, token]);

  const atLeastOneOwnerHasNotification = () => {
    return (
      ownersWithNotifications.some((owner) => owner.receive_questionnaire) &&
      ownersWithNotifications.some(
        (owner) => owner.sign_questionnaire_signature,
      )
    );
  };

  const onSave = async () => {
    if (!selectedCompany) return;

    try {
      setLoading(true);

      await saveOwnerNotifications({
        companyId: selectedCompany.id,
        ownersWithNotifications,
        token,
      });

      addToast('success', 'User notifications saved successfully');
      onClose();
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="User's Notification Settings"
    >
      <Dialog.Content>
        {isLoadingCompany ? (
          <Loading size={20} />
        ) : (
          <Flex isColumn={true} gap={8}>
            <Alert icon="info" type="info">
              If <strong>Get All Users&apos; Signatures</strong> option is
              selected when sending a questionnaire, all active users with{' '}
              <strong>Questionnaire Signature</strong> on will be required to
              sign. Otherwise, only the primary user will be required to sign
            </Alert>

            {!atLeastOneOwnerHasNotification() && (
              <Alert icon="warning" type="warning">
                Please select at least one user to receive notifications for
                both Questionnaire and Questionnaire Signature.
              </Alert>
            )}

            <Flex isColumn={true} gap={8}>
              <Flex isBetween={true} gap={8}>
                <div className={styles.w33} />

                <div className={styles.w33}>
                  <Heading level={5}>Questionnaire Notifications</Heading>

                  <Text type="small" color="secondary">
                    This user will receive a questionnaire link to complete the
                    questionnaire.
                  </Text>
                </div>

                <div className={styles.w33}>
                  <Heading level={5}>
                    Questionnaire Signature Notifications
                  </Heading>

                  <Text type="small" color="secondary">
                    This user will be required to sign all signatures on
                    questionnaires with{' '}
                    <strong>Get All Users&apos; Signatures</strong> on.
                  </Text>
                </div>
              </Flex>
            </Flex>

            {ownersWithNotifications.map((owner) => (
              <Flex isBetween={true} gap={8} key={owner.id}>
                <div className={styles.w33}>
                  <Text>{owner.full_name}</Text>
                </div>

                <div className={styles.w33}>
                  <Checkbox
                    label="Receive Questionnaire"
                    isLabelHidden={true}
                    isChecked={owner.receive_questionnaire}
                    onChange={() => {
                      setOwnersWithNotifications(
                        ownersWithNotifications.map((o) => {
                          if (o.id === owner.id) {
                            return {
                              ...o,
                              receive_questionnaire: !o.receive_questionnaire,
                            };
                          }

                          return o;
                        }),
                      );
                    }}
                  />
                </div>

                <div className={styles.w33}>
                  <Checkbox
                    label="Sign Questionnaire Signature"
                    isLabelHidden={true}
                    isChecked={owner.sign_questionnaire_signature}
                    onChange={(event) => {
                      setOwnersWithNotifications(
                        ownersWithNotifications.map((o) => {
                          if (o.id === owner.id) {
                            return {
                              ...o,
                              sign_questionnaire_signature:
                                event.target.checked,
                            };
                          }

                          return o;
                        }),
                      );
                    }}
                  />
                </div>
              </Flex>
            ))}

            <Separator />

            <ButtonGroup align="right">
              <Button
                value="Cancel"
                intent="secondary"
                onClick={onClose}
                isDisabled={isLoading}
              />

              <Button
                value="Save"
                intent="primary"
                onClick={onSave}
                isDisabled={isLoading || !atLeastOneOwnerHasNotification()}
                isLoading={isLoading}
              />
            </ButtonGroup>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
